import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useBalance } from '../context/BalanceContext';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, Loader, XCircle, AlertTriangle } from 'lucide-react';

const Records = () => {
  const [filter, setFilter] = useState('all');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const { token } = useAuth();
  const { fetchBalance, pendingCount } = useBalance();

  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/api/ratings/user', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setRecords(res.data);
      } catch (err) {
        setRecords([]);
      }
      setLoading(false);
    };
    if (token) fetchRecords();
  }, [token, submittingId]);

  // Filter records based on selected filter type
  const filterRecords = (type) => {
    setFilter(type);
  };

  // Filtering logic
  let filteredRecords = [];
  if (filter === 'all') {
    filteredRecords = records;
  } else if (filter === 'completed') {
    filteredRecords = records.filter(record => record.status.toLowerCase() === 'completed');
  } else if (filter === 'pending') {
    filteredRecords = records.filter(record => record.status.toLowerCase() === 'pending');
  } else if (filter === 'inprogress') {
    filteredRecords = records.filter(record => record.status.toLowerCase() === 'in progress');
  } else if (filter === 'rejected') {
    filteredRecords = records.filter(record => record.status.toLowerCase() === 'rejected');
  }

  const handleSubmitPending = async (ratingId) => {
    setSubmittingId(ratingId);
    try {
      await axios.post(`/api/ratings/submit-pending/${ratingId}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setNotification({ message: 'Pending rating submitted successfully!', type: 'success' });
      await fetchBalance();
    } catch (err) {
      setNotification({
        message: err.response?.data?.message || 'Failed to submit pending rating. Make sure your balance is positive.',
        type: 'error',
      });
    } finally {
      setSubmittingId(null);
    }
  };

  const handleSubmitInProgress = async (ratingId) => {
    setSubmittingId(ratingId);
    try {
      await axios.post(`/api/ratings/submit-pending/${ratingId}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setNotification({ message: 'In Progress rating submitted successfully!', type: 'success' });
      await fetchBalance();
    } catch (err) {
      setNotification({
        message: err.response?.data?.message || 'Failed to submit in progress rating.',
        type: 'error',
      });
    } finally {
      setSubmittingId(null);
    }
  };

  // Effect to clear notification after a few seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const getStatusProps = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return { icon: <CheckCircle size={16} />, color: 'text-green-400', bgColor: 'bg-green-500/10' };
      case 'pending':
        return { icon: <Clock size={16} />, color: 'text-yellow-400', bgColor: 'bg-yellow-500/10' };
      case 'in progress':
        return { icon: <Loader size={16} className="animate-spin" />, color: 'text-blue-400', bgColor: 'bg-blue-500/10' };
      case 'rejected':
        return { icon: <XCircle size={16} />, color: 'text-red-400', bgColor: 'bg-red-500/10' };
      default:
        return { icon: <AlertTriangle size={16} />, color: 'text-gray-400', bgColor: 'bg-gray-500/10' };
    }
  };
  
  const pendingRatingsCount = records.filter(r => r.status.toLowerCase() === 'pending').length;
  const inprogressRatingsCount = records.filter(r => r.status.toLowerCase() === 'in progress').length;

  const filterButtons = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending', count: pendingRatingsCount },
    { key: 'inprogress', label: 'Inprogress', count: inprogressRatingsCount },
    { key: 'completed', label: 'Completed' },
    { key: 'rejected', label: 'Rejected' }
  ];

  return (
    <>
    <Helmet>
      <title>Rating Records | RatingApp</title>
      <meta name="description" content="View your complete app rating history and track your daily progress and earnings on RatingApp." />
    </Helmet>
    <div className="w-full from-gray-900 via-blue-900/30 to-gray-900 pb-28 text-white font-sans">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <motion.h1 
          className="text-4xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 text-center mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Rating History
        </motion.h1>
        <p className="text-slate-400 text-center mb-8">View the status of all your app ratings.</p>

        {/* Notification */}
        <AnimatePresence>
          {notification && (
            <motion.div
              className={`p-4 mb-6 text-sm rounded-lg flex items-center gap-3 ${notification.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}
              role="alert"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              {notification.type === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
              {notification.message}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filter */}
        <motion.div 
          id="records-filter" 
          className="text-white flex justify-center gap-2 md:gap-4 mb-8 bg-gray-800/20 backdrop-blur-xl border border-white/10 p-2 rounded-2xl"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {filterButtons.map(btn => (
            <motion.button
              key={btn.key}
              className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-colors duration-300 relative ${
                filter === btn.key ? 'text-white' : 'text-slate-400 hover:bg-white/5'
              }`}
              onClick={() => filterRecords(btn.key)}
              whileTap={{ scale: 0.95 }}
            >
              {btn.label}
              {btn.count > 0 && (
                <span className="absolute -top-2 -right-2 bg-cyan-500 text-gray-900 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {btn.count}
                </span>
              )}
              {filter === btn.key && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg -z-10"
                  layoutId="activeRecordFilter"
                />
              )}
            </motion.button>
          ))}
        </motion.div>

        {/* Loading State */}
        {loading && (
          <motion.div 
            className="text-slate-400 text-center py-12 flex items-center justify-center gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Loader className="animate-spin" />
            Loading Records...
          </motion.div>
        )}

        {/* Record List */}
        {!loading && filteredRecords.length === 0 && (
          <motion.div 
            className="text-slate-400 text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            No records found for this filter.
          </motion.div>
        )}
        <div className="space-y-4">
          <AnimatePresence>
            {filteredRecords.map((record, index) => {
              const dateObj = new Date(record.createdAt);
              const date = dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
              const time = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
              const statusProps = getStatusProps(record.status);

              return (
                <motion.div
                  key={record._id || index}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-gray-800/20 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <img
                          className="h-14 w-14 object-cover rounded-lg border-2 border-white/10"
                          src={record.appImage || 'https://via.placeholder.com/64'}
                          alt={record.appName}
                        />
                        <div>
                          <h3 className="text-white text-lg font-bold">{record.appName}</h3>
                          <p className="text-slate-400 text-sm">{`${date} at ${time}`}</p>
                        </div>
                      </div>
                      <div className={`flex items-center gap-2 text-sm font-medium px-3 py-1 rounded-full ${statusProps.bgColor} ${statusProps.color}`}>
                        {statusProps.icon}
                        <span className="capitalize">{record.status}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
                      <div className="text-left">
                        <p className="text-slate-400 text-xs">Total Amount</p>
                        <p className="text-white font-mono">{`USDT ${(record.amount || 0).toFixed(2)}`}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-xs">Total Profit</p>
                        <p className="text-green-400 font-mono">{`+${(record.profit || 0).toFixed(2)}`}</p>
                      </div>
                    </div>
                  </div>
                  
                  {record.status.toLowerCase() === 'pending' && (
                    <div className="bg-yellow-500/10 px-5 py-3">
                      <button
                        onClick={() => handleSubmitPending(record._id)}
                        disabled={submittingId === record._id}
                        className="w-full bg-yellow-500 text-yellow-900 font-bold py-2 rounded-lg flex items-center justify-center gap-2 transition-all hover:bg-yellow-400 disabled:opacity-50"
                      >
                        {submittingId === record._id ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            Submitting...
                          </>
                        ) : (
                          'Submit Now'
                        )}
                      </button>
                    </div>
                  )}

                  {record.status.toLowerCase() === 'in progress' && (
                    <div className="bg-blue-500/10 px-5 py-3">
                      <button
                        onClick={() => handleSubmitInProgress(record._id)}
                        disabled={submittingId === record._id}
                        className="w-full bg-blue-500 text-blue-900 font-bold py-2 rounded-lg flex items-center justify-center gap-2 transition-all hover:bg-blue-400 disabled:opacity-50"
                      >
                        {submittingId === record._id ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            Submitting...
                          </>
                        ) : (
                          'Submit Rating'
                        )}
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
    </>
  );
};

export default Records;
